import type { LocationQueryValue } from "vue-router";

/**
 * The single value a page should use for a query param, from whatever vue-router
 * actually hands over.
 *
 * `route.query.x` is typed `LocationQueryValue | LocationQueryValue[]`, and a page
 * that casts it `as string` is making a compile-time promise the runtime does not
 * keep. Three real shapes, all reachable from a crafted or mangled URL:
 *
 * - `?email=x`          → `"x"`
 * - `?email=&email=x`   → `["", "x"]`        — a `.trim()` on this throws TypeError
 * - `?email&email=x`    → `[null, "x"]`      — same, plus a null to skip
 *
 * On `/organisation-sign-up` that TypeError lands in `setup`, so SSR renders an
 * error page: a 500 on a public URL, not a degraded form (Notion 1156 follow-up).
 *
 * Selection rule: the first value that is a non-empty string. Guard and page MUST
 * share this function rather than re-deriving it — a middleware that admits a
 * request on one rule and a page that reads it on another is how the array shape
 * got through the guard and killed the render.
 *
 * **Do not `decodeURI()` the result.** vue-router's `parseQuery` has already
 * percent-decoded it, and its internal decoder swallows its own failure and returns
 * the raw text — so `?firstName=%` arrives as `"%"` and `decodeURI("%")` throws
 * URIError. A second decode can only no-op or throw.
 *
 * Note this does NOT restore a `+` that was decoded to a space; see
 * `emailFromQuery` on `feature/org-invite-prefill-fix` for that, which composes on
 * top of this.
 */
export function firstQueryValue(
  value: LocationQueryValue | LocationQueryValue[] | undefined,
): string {
  if (Array.isArray(value)) {
    return value.find((entry): entry is string => typeof entry === "string" && entry !== "") ?? "";
  }
  return typeof value === "string" ? value : "";
}

export default firstQueryValue;
